import React from 'react';
import { Stack, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@mui/material';
import { Button } from 'components/index';
import { Close } from '@mui/icons-material';
import { getIn, useFormik } from 'formik';
import * as yup from 'yup';
import NumericFormatInput from '../../general/NumericFormatInput';
import { CreateOrUpdateProjectDrawDTO } from './project-draws.type';
import { ProjectLineItemsAndSummary } from '../project.type';
import CustomizedTextField from '../CustomizedTextField';
import { Division } from '../../glossary/glossary.type';
import { createProjectDraw } from './project-draws.api';
import { handleError } from 'common/utils/util.errors';
import UploadDialog from '../../file-upload/UploadDialog';
import { validateDrawUniqueness } from '../project.util';

interface EditProjectDrawDialogProps {
  open: boolean;
  onCancel: () => void;
  initialValues?: CreateOrUpdateProjectDrawDTO;
  setValues: (values: CreateOrUpdateProjectDrawDTO) => void;
  loading?: boolean;
  errorFieldNames: string[];
  // Props for PayApp upload functionality
  projectNumber?: string;
  projectName?: string;
  divisions?: Division[];
  setDetailedDraws: (draws: ProjectLineItemsAndSummary[]) => void;
  existingDraws?: ProjectLineItemsAndSummary[];
}

const EditProjectDrawDialog: React.FC<EditProjectDrawDialogProps> = ({
  open,
  onCancel,
  initialValues,
  setValues,
  loading = false,
  errorFieldNames,
  projectNumber,
  projectName,
  divisions,
  setDetailedDraws,
  existingDraws = [],
}) => {
  // State for PayApp upload functionality
  const [payappUploadDialogOpen, setPayappUploadDialogOpen] = React.useState(false);
  const [createdDraw, setCreatedDraw] = React.useState<ProjectLineItemsAndSummary | null>(null);
  const [uploadPayappLoading, setUploadPayappLoading] = React.useState(false);
  const initialOrDefaultValues: CreateOrUpdateProjectDrawDTO = {
    drawId: initialValues?.drawId,
    projectId: initialValues?.projectId ?? '',
    applicationNumber: initialValues?.applicationNumber ?? '',
    name: initialValues?.name ?? '',
    periodBeginning: initialValues?.periodBeginning ?? undefined,
    periodEnding: initialValues?.periodEnding ?? undefined,
    inputRetainagePercentage: initialValues?.inputRetainagePercentage ?? undefined,
    drawBudget: {
      lessPreviousFundings: initialValues?.drawBudget?.lessPreviousFundings,
      adjustmentsByCDCC: initialValues?.drawBudget?.adjustmentsByCDCC,
      requestedAmount: initialValues?.drawBudget?.requestedAmount,
      estRetainageReleasedThisPeriod: initialValues?.drawBudget?.estRetainageReleasedThisPeriod,
    },
  };

  const formik = useFormik({
    initialValues: initialOrDefaultValues,
    enableReinitialize: true,
    validationSchema: yup.object({
      applicationNumber: yup.string().nullable(),
      name: yup.string().required('Required'),
      inputRetainagePercentage: yup.number().nullable(),
      drawBudget: yup.object({
        lessPreviousFundings: yup.number().nullable(),
        adjustmentsByCDCC: yup.number().nullable(),
        requestedAmount: yup.number().nullable(),
        estRetainageReleasedThisPeriod: yup.number().nullable(),
      }),
    }),
    onSubmit: values => {
      setValues(values);
    },
  });

  const canSubmit = React.useMemo(() => {
    if (!formik.isValid) return false;
    return formik.dirty;
  }, [formik]);

  // Function to handle Upload PayApp button click
  const handleUploadPayappClick = async () => {
    if (!formik.isValid) {
      formik.submitForm(); // This will trigger validation errors
      return;
    }

    // Validate draw uniqueness before creating (applicationNumber is set later via payapp upload)
    const validation = validateDrawUniqueness(existingDraws, formik.values.name || '', '', formik.values.drawId);

    if (!validation.isValid) {
      handleError(new Error(validation.errorMessage));
      return;
    }

    if (!initialValues?.projectId) {
      console.error('Project ID is required to create a budget');
      return;
    }

    setUploadPayappLoading(true);
    try {
      // Create the budget via API
      const createdDrawResponse = await createProjectDraw(initialValues.projectId, formik.values);

      // Convert the API response to the format needed for the upload dialog
      const createdDrawData: ProjectLineItemsAndSummary = {
        projectDrawSummary: {
          id: createdDrawResponse.id,
          name: createdDrawResponse.name,
          applicationNumber: formik.values.applicationNumber || '',
          periodBeginning: createdDrawResponse.periodBeginning || '',
          periodEnding: createdDrawResponse.periodEnding || '',
        },
        lineItems: [],
        budgetSummary: {
          originalContractBudgetSum: 0,
          reallocationsChangeOrders: 0,
          contractBudgetSumToDate: 0,
          totalCompletedAndStored: 0,
          totalRetainageHeld: 0,
          totalRetainageHeldPercentage: 0,
          totalEarnedLessRetainage: 0,
          lessPreviousFundings: formik.values.drawBudget?.lessPreviousFundings || 0,
          currentPaymentDue: 0,
          balanceToCompletePlusRetainage: 0,
          adjustmentsByCDCC: formik.values.drawBudget?.adjustmentsByCDCC || 0,
          totalAmountReccForFunding: 0,
          requestedAmount: formik.values.drawBudget?.requestedAmount || 0,
          difference: 0,
          estRetainageReleasedThisPeriod: formik.values.drawBudget?.estRetainageReleasedThisPeriod || 0,
          totalContractBudgetPerComplete: 0,
        },
        isCreated: false,
        isEdited: false,
        isDeleted: false,
      };

      setCreatedDraw(createdDrawData);
      setDetailedDraws([createdDrawData]);
      setPayappUploadDialogOpen(true);
    } catch (error) {
      handleError(error);
    } finally {
      setUploadPayappLoading(false);
    }
  };

  const closePayappUploadDialog = () => {
    setPayappUploadDialogOpen(false);
    setCreatedDraw(null);
  };

  return (
    <Dialog open={open} onClose={onCancel} fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        {initialValues?.drawId ? 'Update G702/3' : 'Add New G702/3'}
        <IconButton onClick={onCancel} sx={{ marginLeft: 'auto' }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={formik.handleSubmit}>
          <Stack spacing={2} py={2}>
            <CustomizedTextField
              name='name'
              label='Name (Contractor, if multiple)'
              value={formik.values.name}
              onChange={e => {
                formik.setFieldValue('name', e.target.value.trimStart());
              }}
              onBlur={e => {
                formik.setFieldValue('name', e.target.value.trim());
                formik.handleBlur(e);
              }}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              InputLabelProps={{ shrink: true }}
              required
              errorFieldNames={errorFieldNames}
            />
            {initialValues?.drawId && (
              <>
                <CustomizedTextField
                  name='drawBudget.adjustmentsByCDCC'
                  label='Adjustments by CDCC'
                  value={formik.values.drawBudget?.adjustmentsByCDCC ?? ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    getIn(formik.touched.drawBudget, 'adjustmentsByCDCC') && Boolean(getIn(formik.errors.drawBudget, 'adjustmentsByCDCC'))
                  }
                  helperText={
                    getIn(formik.touched.drawBudget, 'adjustmentsByCDCC') ? getIn(formik.errors.drawBudget, 'adjustmentsByCDCC') : ''
                  }
                  InputProps={{
                    inputComponent: NumericFormatInput as any,
                  }}
                  InputLabelProps={{ shrink: true }}
                  errorFieldNames={errorFieldNames}
                />
                <CustomizedTextField
                  name='drawBudget.estRetainageReleasedThisPeriod'
                  label='Est. Retainage Released This Period'
                  value={formik.values.drawBudget?.estRetainageReleasedThisPeriod ?? ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    getIn(formik.touched.drawBudget, 'estRetainageReleasedThisPeriod') &&
                    Boolean(getIn(formik.errors.drawBudget, 'estRetainageReleasedThisPeriod'))
                  }
                  helperText={
                    getIn(formik.touched.drawBudget, 'estRetainageReleasedThisPeriod')
                      ? getIn(formik.errors.drawBudget, 'estRetainageReleasedThisPeriod')
                      : ''
                  }
                  InputProps={{
                    inputComponent: NumericFormatInput as any,
                  }}
                  InputLabelProps={{ shrink: true }}
                  errorFieldNames={errorFieldNames}
                />
              </>
            )}
          </Stack>
        </form>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' color='error' onClick={onCancel}>
          Cancel
        </Button>
        <Button variant='contained' onClick={formik.submitForm} disabled={!canSubmit} color='primary' loading={loading}>
          {initialValues?.drawId ? 'Update' : 'Add'}
        </Button>
        {/* Only show Upload PayApp button when creating new budget (not updating) */}
        {!initialValues?.drawId && (
          <Button
            variant='contained'
            onClick={handleUploadPayappClick}
            disabled={!canSubmit || !formik.isValid}
            color='secondary'
            loading={uploadPayappLoading}
          >
            Upload PayApp
          </Button>
        )}
      </DialogActions>

      {/* PayApp Upload Dialog */}
      {createdDraw && (
        <UploadDialog
          open={payappUploadDialogOpen}
          onClose={closePayappUploadDialog}
          filesToAccept={{ 'application/pdf': ['.pdf'] }}
          projectId={initialValues?.projectId}
          projectNumber={projectNumber}
          projectName={projectName}
          payappDrawId={createdDraw.projectDrawSummary.id}
          projectDraw={createdDraw}
          divisions={divisions}
          hideTabs={true}
          defaultTab={1}
        />
      )}
    </Dialog>
  );
};

export default EditProjectDrawDialog;
